import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Shield, ArrowLeft } from "lucide-react";
import KeysTab from "../components/admin/KeysTab";
import ModelsTab from "../components/admin/ModelsTab";
import UsersTab from "../components/admin/UsersTab";
import PromptsTab from "../components/admin/PromptsTab";
import VoiceTab from "../components/admin/VoiceTab";
import UsageTab from "../components/admin/UsageTab";
import EmailTab from "../components/admin/EmailTab";
import BillingTab from "../components/admin/BillingTab";
import BrandingTab from "../components/admin/BrandingTab";
import ContentTab from "../components/admin/ContentTab";
import OperationalModelsTab from "../components/admin/OperationalModelsTab";
import EvalTab from "../components/admin/EvalTab";
import UserMenu from "../components/UserMenu";

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border h-14 flex items-center justify-between px-4 lg:px-6 bg-background">
        <div className="flex items-center gap-4">
          <Link to="/workspace" className="flex items-center gap-2 text-muted-foreground hover:text-foreground" data-testid="admin-back">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm font-mono">workspace</span>
          </Link>
          <div className="h-4 w-px bg-border" />
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" />
            <span className="font-display font-bold tracking-tight">Super Admin</span>
            <span className="text-xs font-mono text-muted-foreground">/ {user?.email}</span>
          </div>
        </div>
        <UserMenu variant="admin" />
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground font-mono">
          [ control room ]
        </div>
        <h1 className="font-display font-black tracking-tight text-3xl lg:text-4xl mt-2">
          Configure Nexa
        </h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Manage API integrations, model aliases (up to 10), and users. Keys are encrypted at rest and never shown back to you in full.
        </p>

        <Tabs defaultValue="keys" className="mt-10">
          <TabsList className="rounded-sm">
            <TabsTrigger value="keys" data-testid="tab-keys" className="rounded-sm">API Keys</TabsTrigger>
            <TabsTrigger value="models" data-testid="tab-models" className="rounded-sm">Models</TabsTrigger>
            <TabsTrigger value="op-models" data-testid="tab-op-models" className="rounded-sm">Operational</TabsTrigger>
            <TabsTrigger value="eval" data-testid="tab-eval" className="rounded-sm">Eval</TabsTrigger>
            <TabsTrigger value="prompts" data-testid="tab-prompts" className="rounded-sm">Prompts</TabsTrigger>
            <TabsTrigger value="voice" data-testid="tab-voice" className="rounded-sm">Voice</TabsTrigger>
            <TabsTrigger value="email" data-testid="tab-email" className="rounded-sm">Email</TabsTrigger>
            <TabsTrigger value="billing" data-testid="tab-billing" className="rounded-sm">Billing</TabsTrigger>
            <TabsTrigger value="usage" data-testid="tab-usage" className="rounded-sm">Usage</TabsTrigger>
            <TabsTrigger value="branding" data-testid="tab-branding" className="rounded-sm">Branding</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content" className="rounded-sm">Content</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users" className="rounded-sm">Users</TabsTrigger>
          </TabsList>
          <TabsContent value="keys" className="mt-8">
            <KeysTab />
          </TabsContent>
          <TabsContent value="models" className="mt-8">
            <ModelsTab />
          </TabsContent>
          <TabsContent value="op-models" className="mt-8">
            <OperationalModelsTab />
          </TabsContent>
          <TabsContent value="eval" className="mt-8">
            <EvalTab />
          </TabsContent>
          <TabsContent value="prompts" className="mt-8">
            <PromptsTab />
          </TabsContent>
          <TabsContent value="voice" className="mt-8">
            <VoiceTab />
          </TabsContent>
          <TabsContent value="email" className="mt-8">
            <EmailTab />
          </TabsContent>
          <TabsContent value="billing" className="mt-8">
            <BillingTab />
          </TabsContent>
          <TabsContent value="usage" className="mt-8">
            <UsageTab />
          </TabsContent>
          <TabsContent value="branding" className="mt-8">
            <BrandingTab />
          </TabsContent>
          <TabsContent value="content" className="mt-8">
            <ContentTab />
          </TabsContent>
          <TabsContent value="users" className="mt-8">
            <UsersTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
